<template>
  <div class="journal-entry-container journal-entry-page--glass glass-tab-page">
    <BrucdcumHeader
      :breadcrumb="['Company', 'Journal Entry']"
      :show-back-button="true"
      :back-fallback="backFallbackRoute"
    />

    <div class="glass-tab-view glass-tab-view--flush journal-entry-glass-shell">
      <div class="glass-tab-view__content journal-entry-page-stack">
        <div class="journal-entry-header-block">
          <div class="journal-entry-title-row">
            <h2 class="journal-entry-title">Journal Entry</h2>
            <SecondaryButton
              label=""
              icon="bi bi-question-circle"
              custom-class="journal-entry-help-btn"
              title="Journal entry help"
              aria-label="Show journal entry help"
              @click="showHelpModal"
            />
          </div>
          <p class="journal-entry-subtitle">
            Record double-entry transactions with balanced debits and credits
          </p>
        </div>

        <div class="journal-entry-meta-panel glass-tab-panel--nested">
          <LabelDateField
            input-id="journal-entry-date"
            label="Date"
            v-model="date"
            placeholder="MM/DD/YYYY"
            flex
            required
          />
          <LabelInputField
            input-id="journal-entry-no"
            label="No"
            :model-value="entryNo"
            readonly
            flex
            required
            :placeholder="entryNoPlaceholder"
            :custom-class="isLoadingEntryNo ? 'journal-entry-entry-no--loading' : ''"
          />
        </div>

        <div class="journal-entry-table-panel glass-tab-panel--nested">
          <h3 class="journal-entry-table-heading">Entries</h3>
          <div v-if="validationError" class="journal-entry-validation">
            <span class="journal-entry-validation-error">{{ validationError }}</span>
          </div>
          <div class="journal-entry-table-section consultant-glass-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table journal-entry-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Memo</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th class="journal-entry-table-actions-cell"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="rows.length === 0">
                      <td colspan="5" class="consultant-glass-table-no-records">No records to show</td>
                    </tr>
                    <tr
                      v-for="(row, idx) in rows"
                      :key="idx"
                      class="consultant-glass-table-row"
                      :style="{ animationDelay: `${0.03 + idx * 0.025}s` }"
                    >
                      <td class="journal-entry-account-cell">
                        <LabelDropdown
                          :select-id="`journal-entry-account-${idx}`"
                          label="Account"
                          hide-label
                          :model-value="row.account"
                          :options="accountOptions"
                          flex
                          searchable
                          custom-class="journal-entry-account-dropdown"
                          menu-class="journal-entry-account-dropdown-menu"
                          @update:model-value="onAccountSelect(row, $event)"
                        />
                      </td>
                      <td>
                        <input
                          v-model="row.memo"
                          type="text"
                          placeholder="Memo"
                          class="journal-entry-cell-control"
                        />
                      </td>
                      <td>
                        <input
                          v-model.number="row.debit"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          class="journal-entry-cell-control journal-entry-cell-amount"
                          @input="handleDebitInput(row, $event.target.value)"
                        />
                      </td>
                      <td>
                        <input
                          v-model.number="row.credit"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          class="journal-entry-cell-control journal-entry-cell-amount"
                          @input="handleCreditInput(row, $event.target.value)"
                        />
                      </td>
                      <td class="journal-entry-table-actions-cell">
                        <SecondaryButton
                          label="Delete"
                          icon="bi bi-trash"
                          compact
                          custom-class="journal-entry-delete-btn"
                          title="Delete row"
                          :disabled="rows.length <= 2"
                          @click="deleteRow(idx)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="journal-entry-add-row">
            <PrimaryButton
              label="Add Row"
              icon="bi bi-plus-lg"
              compact
              @click="addRow"
            />
          </div>
        </div>

        <div class="journal-entry-comments-panel glass-tab-panel--nested">
          <LabelDescriptionField
            input-id="journal-entry-comments"
            label="Comments"
            v-model="comments"
            placeholder="Write comments for this journal entry"
            :rows="3"
            flex
          />
        </div>

        <div class="journal-entry-totals-panel glass-tab-panel--nested">
          <span class="journal-entry-total-item journal-entry-total-item--debit">
            Debit Total ($): <b>{{ debitTotal.toFixed(2) }}</b>
          </span>
          <span class="journal-entry-total-item journal-entry-total-item--credit">
            Credit Total ($): <b>{{ creditTotal.toFixed(2) }}</b>
          </span>
          <span v-if="!isBalanced" class="journal-entry-balance-error">Totals must be equal</span>
          <span v-else class="journal-entry-balance-success">Balanced</span>
        </div>

        <div class="journal-entry-actions glass-tab-view__actions">
          <SecondaryButton label="Cancel" @click="cancel" />
          <SecondaryButton
            label="Make it recurring"
            icon="bi bi-arrow-repeat"
            :disabled="!canSave || isSaving"
            @click="makeRecurring"
          />
          <PrimaryButton
            label="Save and New"
            icon="bi bi-file-earmark-plus"
            :disabled="!canSave"
            :loading="isSaving && savingAction === 'new'"
            @click="saveAndNew"
          />
          <PrimaryButton
            label="Save and Close"
            icon="bi bi-check-lg"
            :disabled="!canSave"
            :loading="isSaving && savingAction === 'close'"
            @click="saveAndClose"
          />
        </div>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="journal-entry-help-fade">
        <div v-if="showHelp" class="journal-entry-help-overlay" @click="hideHelpModal">
          <div class="journal-entry-help-modal" role="dialog" aria-modal="true" aria-labelledby="journal-entry-help-title" @click.stop>
            <div class="journal-entry-help-modal-header">
              <h3 id="journal-entry-help-title">Journal Entry Help</h3>
              <button type="button" class="journal-entry-help-modal-close" aria-label="Close help" @click="hideHelpModal">&times;</button>
            </div>
            <div class="journal-entry-help-modal-content">
          <h4>What is a Journal Entry?</h4>
          <p>A Journal Entry is a fundamental accounting record that documents business transactions in chronological order. It follows the double-entry bookkeeping system, where every transaction affects at least two accounts with equal debits and credits.</p>
          
          <h4>Double-Entry Bookkeeping Principle</h4>
          <p><strong>For every transaction: Debits = Credits</strong></p>
          <p>This fundamental principle ensures that the accounting equation (Assets = Liabilities + Equity) always remains in balance.</p>
          
          <h4>Understanding Debits and Credits</h4>
          <div class="journal-entry-help-account-types">
            <div class="journal-entry-help-section">
              <h5>Debit Increases (Normal Debit Balance)</h5>
              <ul>
                <li><strong>Assets</strong> - Cash, Bank, Inventory, Equipment</li>
                <li><strong>Expenses</strong> - Operating costs, salaries, rent</li>
                <li><strong>Cost of Goods Sold</strong> - Direct costs of products</li>
              </ul>
            </div>
            
            <div class="journal-entry-help-section">
              <h5>Credit Increases (Normal Credit Balance)</h5>
              <ul>
                <li><strong>Liabilities</strong> - Loans, credit cards, bills payable</li>
                <li><strong>Equity</strong> - Owner's capital, retained earnings</li>
                <li><strong>Income</strong> - Sales revenue, service income</li>
              </ul>
            </div>
          </div>
          
          <h4>Journal Entry Components</h4>
          <ul>
            <li><strong>Date</strong>: When the transaction occurred</li>
            <li><strong>Entry Number</strong>: Unique identifier (auto-generated)</li>
            <li><strong>Account</strong>: The account being debited or credited</li>
            <li><strong>Memo</strong>: Description or notes about the transaction</li>
            <li><strong>Debit Amount</strong>: Amount to be debited (left side)</li>
            <li><strong>Credit Amount</strong>: Amount to be credited (right side)</li>
            <li><strong>Comments</strong>: Additional notes about the entire entry</li>
          </ul>
          
          <h4>Creating a Valid Journal Entry</h4>
          <ol>
            <li><strong>Minimum Two Lines</strong>: Every journal entry must have at least two lines</li>
            <li><strong>Account Selection</strong>: Each line must have an account selected</li>
            <li><strong>Debit OR Credit</strong>: Each line should have either a debit OR credit amount, not both</li>
            <li><strong>Balanced Totals</strong>: Total debits must equal total credits</li>
            <li><strong>At Least One Debit</strong>: Must have at least one debit entry</li>
            <li><strong>At Least One Credit</strong>: Must have at least one credit entry</li>
          </ol>
          
          <h4>Common Journal Entry Examples</h4>
          <div class="journal-entry-help-section">
            <h5>Example 1: Purchase Equipment with Cash</h5>
            <ul>
              <li><strong>Debit</strong>: Equipment (Asset) - $5,000</li>
              <li><strong>Credit</strong>: Cash (Asset) - $5,000</li>
              <li><strong>Effect</strong>: Equipment increases, Cash decreases</li>
            </ul>
          </div>
          
          <div class="journal-entry-help-section">
            <h5>Example 2: Pay Rent Expense</h5>
            <ul>
              <li><strong>Debit</strong>: Rent Expense (Expense) - $1,200</li>
              <li><strong>Credit</strong>: Cash (Asset) - $1,200</li>
              <li><strong>Effect</strong>: Rent expense increases, Cash decreases</li>
            </ul>
          </div>
          
          <div class="journal-entry-help-section">
            <h5>Example 3: Receive Payment for Services</h5>
            <ul>
              <li><strong>Debit</strong>: Cash (Asset) - $3,000</li>
              <li><strong>Credit</strong>: Service Income (Income) - $3,000</li>
              <li><strong>Effect</strong>: Cash increases, Income increases</li>
            </ul>
          </div>
          
          <h4>Validation Rules</h4>
          <ul>
            <li><strong>Balance Check</strong>: Debit total must equal credit total</li>
            <li><strong>Account Required</strong>: All lines must have an account selected</li>
            <li><strong>Amount Required</strong>: Each line must have either debit or credit amount</li>
            <li><strong>No Double Entry</strong>: Cannot have both debit and credit on same line</li>
            <li><strong>Minimum Entries</strong>: At least two lines required</li>
          </ul>
          
          <h4>Account Types Available</h4>
          <div class="journal-entry-help-account-types">
            <div class="journal-entry-help-section">
              <h5>Asset Accounts</h5>
              <ul>
                <li><strong>Cash</strong> - Money in bank accounts and petty cash</li>
                <li><strong>Bank</strong> - Checking and savings accounts</li>
                <li><strong>Other current Asset</strong> - Short-term assets like prepaid expenses</li>
                <li><strong>Inventory Asset</strong> - Products and materials held for sale</li>
                <li><strong>Other Assets</strong> - Miscellaneous assets</li>
                <li><strong>Fixed Asset</strong> - Long-term assets like equipment and buildings</li>
              </ul>
            </div>
            
            <div class="journal-entry-help-section">
              <h5>Liability & Equity Accounts</h5>
              <ul>
                <li><strong>Credit Card</strong> - Outstanding credit card balances</li>
                <li><strong>Payroll Liability</strong> - Wages and taxes owed to employees</li>
                <li><strong>Current Liability</strong> - Short-term debts due within one year</li>
                <li><strong>Long Term Liability</strong> - Long-term debts like loans</li>
                <li><strong>Equity</strong> - Owner's capital and retained earnings</li>
              </ul>
            </div>
            
            <div class="journal-entry-help-section">
              <h5>Income & Expense Accounts</h5>
              <ul>
                <li><strong>Income</strong> - Revenue from sales and services</li>
                <li><strong>Other Income</strong> - Additional revenue sources</li>
                <li><strong>Expense</strong> - Operating costs and expenses</li>
                <li><strong>Other Expense</strong> - Additional costs</li>
                <li><strong>Cost of Goods Sold</strong> - Direct costs of producing goods</li>
              </ul>
            </div>
          </div>
          
          <h4>Tips for Creating Journal Entries</h4>
          <ul>
            <li><strong>Start with the obvious</strong>: Identify which account is being increased or decreased</li>
            <li><strong>Use the opposite entry</strong>: For every debit, there must be an equal credit</li>
            <li><strong>Think about the accounting equation</strong>: Assets = Liabilities + Equity</li>
            <li><strong>Use descriptive memos</strong>: Help others understand the transaction</li>
            <li><strong>Review before saving</strong>: Double-check that debits equal credits</li>
          </ul>
          
          <h4>After Saving</h4>
          <ul>
            <li><strong>Posted Status</strong>: Journal entries are automatically posted</li>
            <li><strong>Account Balances</strong>: Affected account balances are updated</li>
            <li><strong>Audit Trail</strong>: Entry is recorded in the general ledger</li>
            <li><strong>Reports</strong>: Available in financial reports and statements</li>
          </ul>
        </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <SuccessModal
      :show="showSuccessModal"
      :title="successModalTitle"
      :message="successModalMessage"
      :button-text="successModalButtonText"
      @close="handleSuccessModalClose"
    />

    <UnsavedChangesModal
      :show="showUnsavedChangesModal"
      @stay="handleStayOnPage"
      @leave="handleLeavePage"
    />
  </div>
</template>

<script>
import '../styles/JournalEntry.css';
import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';
import JournalEntryService from '@/services/journalEntry.service.js';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import UnsavedChangesModal from '@/modules/settings/components/UnsavedChangesModal.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'JournalEntry',
  components: {
    BrucdcumHeader,
    SuccessModal,
    UnsavedChangesModal,
    LabelDateField,
    LabelInputField,
    LabelDescriptionField,
    LabelDropdown,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      date: new Date().toISOString().split('T')[0], // Set current date as default
      entryNo: '',
      comments: '',
      rows: [
        { account: '', memo: '', debit: '', credit: '' },
        { account: '', memo: '', debit: '', credit: '' }
      ],
      accounts: [],
      validationError: '',
      isBalanced: false,
      isLoadingEntryNo: false,
      isSaving: false,
      savingAction: null,
      error: null,
      showHelp: false,
      skipUnsavedPrompt: false,
      showUnsavedChangesModal: false,
      pendingNavigation: null,
      showSuccessModal: false,
      successModalTitle: 'Success',
      successModalMessage: '',
      successModalButtonText: 'OK'
    };
  },
  computed: {
    entryNoPlaceholder() {
      if (this.isLoadingEntryNo) return 'Generating...';
      return this.entryNo ? '' : 'Auto-generated';
    },
    backFallbackRoute() {
      const companyId = this.$route.params.id || this.companyId;
      return companyId ? `/company/${companyId}/dashboard/overview` : '/home';
    },
    accountOptions() {
      const placeholder = { value: '', label: 'Select account' };
      const items = (this.accounts || []).map((account) => ({
        value: account.id,
        label: `${account.accountName} (${account.accountNumber}) - ${account.accountType}`,
      }));
      return [placeholder, ...items];
    },
    debitTotal() {
      return this.rows.reduce((sum, row) => sum + (parseFloat(row.debit) || 0), 0);
    },
    creditTotal() {
      return this.rows.reduce((sum, row) => sum + (parseFloat(row.credit) || 0), 0);
    },
    canSave() {
      return this.rows.length >= 2 &&
             this.isBalanced &&
             this.date &&
             this.rows.every(row => row.account) &&
             !this.validationError;
    },
    hasChanges() {
      return this.rows.some(row => 
        row.account || row.memo || row.debit || row.credit
      ) || this.comments.trim() !== '';
    },
    // Get company ID from multiple sources following CreateCompany.vue pattern
    companyId() {
      // First try to get from Vuex store (like CreateCompany.vue)
      const storeCompany = this.$store.state.companies?.currentCompany;
      if (storeCompany && storeCompany.id) {
        return storeCompany.id;
      }
      
      // Also try global store state
      const globalCompany = this.$store.state.currentCompany;
      if (globalCompany && globalCompany.id) {
        return globalCompany.id;
      }
      
      // Then try route parameters
      const routeCompanyId = this.$route?.params?.companyId || this.$route?.params?.id;
      if (routeCompanyId) {
        return routeCompanyId;
      }
      
      // Finally try to get from localStorage or other sources
      const localCompanyId = localStorage.getItem('currentCompanyId');
      if (localCompanyId) {
        return localCompanyId;
      }
      
      return null;
    }
  },
  methods: {
    onAccountSelect(row, value) {
      row.account = value;
      this.validateEntries();
    },
    addRow() {
      this.rows.push({ account: '', memo: '', debit: '', credit: '' });
      this.validateEntries();
    },
    deleteRow(idx) {
      if (this.rows.length > 2) {
        this.rows.splice(idx, 1);
        this.validateEntries();
      }
    },
    validateEntries() {
      this.validationError = '';
      
      // Check minimum entries
      if (this.rows.length < 2) {
        this.validationError = 'At least two entries are required';
        return;
      }

      // Check if all accounts are selected
      const unselectedAccounts = this.rows.filter(row => !row.account);
      if (unselectedAccounts.length > 0) {
        this.validationError = 'All accounts must be selected';
        return;
      }

      // Check if each row has either debit OR credit (not both)
      const invalidRows = this.rows.filter(row => {
        const debit = parseFloat(row.debit) || 0;
        const credit = parseFloat(row.credit) || 0;
        return (debit > 0 && credit > 0) || (debit === 0 && credit === 0);
      });
      
      if (invalidRows.length > 0) {
        this.validationError = 'Each entry must have either a debit OR credit amount, not both';
        return;
      }

      // Check if at least one debit and one credit entry
      const hasDebit = this.rows.some(row => (parseFloat(row.debit) || 0) > 0);
      const hasCredit = this.rows.some(row => (parseFloat(row.credit) || 0) > 0);
      
      if (!hasDebit || !hasCredit) {
        this.validationError = 'At least one debit and one credit entry are required';
        return;
      }

      // Check balance
      const debitTotal = this.debitTotal;
      const creditTotal = this.creditTotal;
      
      if (Math.abs(debitTotal - creditTotal) > 0.01) {
        this.isBalanced = false;
        this.validationError = `Totals must be equal. Debit: $${debitTotal.toFixed(2)}, Credit: $${creditTotal.toFixed(2)}`;
        return;
      }

      this.isBalanced = true;
    },
    handleDebitInput(row, value) {
      if (value && parseFloat(value) > 0) {
        row.credit = '';
      }
      this.validateEntries();
    },
    handleCreditInput(row, value) {
      if (value && parseFloat(value) > 0) {
        row.debit = '';
      }
      this.validateEntries();
    },
    async generateEntryNumber() {
      this.isLoadingEntryNo = true;
      this.entryNo = ''; // Clear current entry number immediately
      
      try {
        logger.log('Generating entry number for company:', this.companyId);
        
        if (!this.companyId) {
          throw new Error('Company ID not available');
        }
        
        const response = await JournalEntryService.generateNextEntryNumber(this.companyId, 'JE');
        logger.log('Entry number response:', response);
        
        // Force reactivity update
        this.$nextTick(() => {
          this.entryNo = response.nextEntryNo;
          logger.log('Generated entry number:', this.entryNo);
        });
      } catch (error) {
        logger.error('Error generating entry number:', error);
        // Fallback to a default format if API fails
        this.$nextTick(() => {
          this.entryNo = 'JE-001';
          logger.log('Using fallback entry number:', this.entryNo);
        });
      } finally {
        this.isLoadingEntryNo = false;
      }
    },
    async saveAndClose() {
      if (this.isSaving) return;
      if (!this.canSave) {
        this.$toast?.error('Please fix all validation errors before saving.') || alert('Please fix all validation errors before saving.');
        return;
      }

      try {
        if (!this.companyId) {
          this.$toast?.error('Company ID not found. Please try again.') || alert('Company ID not found. Please try again.');
          return;
        }

        this.isSaving = true;
        this.savingAction = 'close';

        const journalEntryData = {
          entryDate: this.date,
          comments: this.comments,
          entries: this.rows.map(row => ({
            account: row.account,
            memo: row.memo,
            debit: parseFloat(row.debit) || 0,
            credit: parseFloat(row.credit) || 0
          }))
        };

        const response = await JournalEntryService.createJournalEntry(this.companyId, journalEntryData, 'JE');

        if (this.$toast) {
          this.$toast.success('Journal entry saved successfully!');
        }

        this.successModalTitle = 'Journal Entry Saved';
        this.successModalMessage = `Journal entry ${response.journalEntry.entryNo} saved successfully.`;
        this.successModalButtonText = 'OK';
        this.showSuccessModal = true;
      } catch (error) {
        logger.error('Error saving journal entry:', error);
        this.handleError(error);
      } finally {
        this.isSaving = false;
        this.savingAction = null;
      }
    },
    async saveAndNew() {
      if (this.isSaving) return;
      if (!this.canSave) {
        this.$toast?.error('Please fix all validation errors before saving.') || alert('Please fix all validation errors before saving.');
        return;
      }

      try {
        if (!this.companyId) {
          this.$toast?.error('Company ID not found. Please try again.') || alert('Company ID not found. Please try again.');
          return;
        }

        this.isSaving = true;
        this.savingAction = 'new';

        const journalEntryData = {
          entryDate: this.date,
          comments: this.comments,
          entries: this.rows.map(row => ({
            account: row.account,
            memo: row.memo,
            debit: parseFloat(row.debit) || 0,
            credit: parseFloat(row.credit) || 0
          }))
        };

        const response = await JournalEntryService.createJournalEntry(this.companyId, journalEntryData, 'JE');

        this.$toast?.success('Journal entry saved successfully! Creating new entry...') || alert(`Journal entry saved successfully!\nEntry Number: ${response.journalEntry.entryNo}\n\nCreating new entry...`);

        await this.resetForm();
      } catch (error) {
        logger.error('Error saving journal entry:', error);
        this.handleError(error);
      } finally {
        this.isSaving = false;
        this.savingAction = null;
      }
    },
    cancel() {
      if (this.hasChanges) {
        this.pendingNavigation = { path: '/dashboard' };
        this.showUnsavedChangesModal = true;
      } else {
        this.$router.push('/dashboard');
      }
    },
    handleStayOnPage() {
      this.showUnsavedChangesModal = false;
      this.pendingNavigation = null;
    },
    handleLeavePage() {
      this.showUnsavedChangesModal = false;
      this.skipUnsavedPrompt = true;
      const dest = this.pendingNavigation;
      this.pendingNavigation = null;
      if (dest?.fullPath) {
        this.$router.push(dest.fullPath);
      } else if (dest?.path) {
        this.$router.push(dest.path);
      } else {
        this.$router.push('/dashboard');
      }
    },
    makeRecurring() {
      this.$toast?.info('Recurring journal entry feature coming soon!') || alert('Recurring journal entry feature coming soon!');
    },
    async resetForm() {
      this.entryNo = '';
      this.date = new Date().toISOString().split('T')[0];
      this.comments = '';
      this.rows = [
        { account: '', memo: '', debit: '', credit: '' },
        { account: '', memo: '', debit: '', credit: '' }
      ];
      this.validationError = '';
      this.isBalanced = false;
      this.skipUnsavedPrompt = false;
      
      // Generate new entry number immediately
      await this.generateEntryNumber();
    },
    // Error handling method following CreateCompany.vue pattern
    handleError(error) {
      logger.error('Error:', error);
      
      if (error.message.includes('Please log in')) {
        this.$toast?.error('Please log in to continue') || alert('Please log in to continue');
        this.$router.push('/login');
      } else if (error.message.includes('\n')) {
        // Multiple validation errors
        const errors = error.message.split('\n');
        errors.forEach(err => {
          this.$toast?.error(err) || alert(err);
        });
      } else if (error.message.includes('Server error')) {
        this.$toast?.error('Unable to save journal entry. Please try again later.') || alert('Unable to save journal entry. Please try again later.');
      } else if (error.message.includes('No response')) {
        this.$toast?.error('Unable to connect to the server. Please check your internet connection.') || alert('Unable to connect to the server. Please check your internet connection.');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'An error occurred while saving the journal entry.';
        this.$toast?.error(errorMessage) || alert(`Error: ${errorMessage}`);
      }
    },
    // Load company data following CreateCompany.vue pattern
    async loadCompanyData() {
      try {
        logger.log('Loading company data...');
        
        if (!this.companyId) {
          logger.warn('No company ID available');
          return;
        }

        logger.log('Company ID:', this.companyId);

        // Try to get company data from store first
        let companyData = this.$store.state.companies?.currentCompany;
        logger.log('Company data from store:', companyData);
        
        if (!companyData) {
          logger.log('Fetching company data from API...');
          // If not in store, try to fetch from API using ApiService instead of CompanyService
          const response = await ApiService.getCompany(this.companyId);
          companyData = response.data;
          logger.log('Company data from API:', companyData);
          
          // Store in Vuex if available
          if (companyData) {
            this.$store.commit('companies/SET_CURRENT_COMPANY', companyData);
            logger.log('Stored company data in Vuex');
          }
        }

        // Store company ID in localStorage for persistence
        if (this.companyId) {
          localStorage.setItem('currentCompanyId', this.companyId);
          logger.log('Stored company ID in localStorage');
        }

      } catch (error) {
        logger.error('Error loading company data:', error);
        this.handleError(error);
      }
    },
    showHelpModal() {
      this.showHelp = true;
    },
    hideHelpModal() {
      this.showHelp = false;
    },
    handleSuccessModalClose() {
      this.showSuccessModal = false;
      this.skipUnsavedPrompt = true;
      this.$nextTick(() => {
        this.$router.back();
      });
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.skipUnsavedPrompt) {
      next();
      return;
    }

    if (this.hasChanges) {
      this.pendingNavigation = {
        path: to.path,
        fullPath: to.fullPath,
        query: to.query,
        params: to.params
      };
      this.showUnsavedChangesModal = true;
      next(false);
    } else {
      next();
    }
  },
  async created() {
    try {
      logger.log('JournalEntry component created');
      logger.log('Route params:', this.$route.params);
      logger.log('Store state:', this.$store.state);
      
      // Load company data first
      await this.loadCompanyData();
      
      if (this.companyId) {
        logger.log('Loading accounts for company:', this.companyId);
        const res = await ApiService.getCompanyAccounts(this.companyId);
        this.accounts = res.data.accounts || res.data || [];
        logger.log('Loaded accounts:', this.accounts.length);
        await this.generateEntryNumber();
      } else {
        logger.error('No company ID available for loading accounts');
        this.$toast?.error('Unable to load company data. Please try again.') || alert('Unable to load company data. Please try again.');
      }
    } catch (e) {
      logger.error('Error loading accounts:', e);
      this.accounts = [];
      this.handleError(e);
    }
  },
  mounted() {
    this.validateEntries();
  }
};
</script> 